/**
 * Alpine.js Store for Admin Bar
 *
 * Manages the state of all popups and their targeting results.
 *
 * Server-side conditions have `serverResult` attached directly to each condition
 * by PHP. The JS condition-registry uses these pre-computed results automatically,
 * so no merging is needed - the evaluation results already include server results.
 */

import type { GroupEvaluationResult } from '../shared/types/targeting';
import type { AdminBarConfig, AdminBarFeed } from './index';
import type {
	TriggerState,
	PopupLifecycleState,
} from '../frontend/triggers/trigger-state-broadcaster';
import type { PopupConfig } from '../shared/types/popup';
import { getCookie } from '../frontend/utils/cookies';
import { getInitialPanelOpenState, savePanelOpenState } from './components';

/**
 * Get the popup config from window.gpPopupsConfig for a given feed ID.
 * Returns undefined if not found.
 */
function getPopupConfigForFeed(feedId: number): PopupConfig | undefined {
	return window?.gpPopupsConfig?.find((config) => config.feedId === feedId);
}

/**
 * Build initial trigger state from popup config.
 * This provides the trigger info immediately without waiting for events.
 */
function buildInitialTriggerState(feedId: number): TriggerState | null {
	const config = getPopupConfigForFeed(feedId);
	if (!config?.trigger) {
		return null;
	}

	const trigger = config.trigger;
	const triggerType = trigger.type;

	// Build initial progress based on trigger type using discriminated union narrowing
	const progress: TriggerState['progress'] = {};

	switch (trigger.type) {
		case 'timeout':
			progress.totalMs = trigger.delay * 1000;
			progress.remainingMs = progress.totalMs;
			break;
		case 'scroll_depth':
			progress.currentValue = 0;
			progress.targetValue = trigger.scrollDepthValue;
			progress.unit = trigger.scrollDepthUnit;
			break;
		case 'button':
			progress.selector = trigger.selector;
			break;
		// exit_intent and js_api don't have progress info
	}

	return {
		feedId,
		triggerType,
		state: 'waiting',
		progress,
	};
}

export interface AdminBarFeedState extends AdminBarFeed {
	/** Results from client-side evaluation (includes serverResult from conditions) */
	evaluationResults: GroupEvaluationResult[];
	/** Whether the popup would display on this page */
	overallMatch: boolean;
	/** Current trigger state and progress */
	triggerState: TriggerState | null;
	/** Current popup lifecycle state */
	popupState: PopupLifecycleState;
	/** Whether the popup has been dismissed (reactive, updated from cookie and popup state) */
	isDismissedByCookie: boolean;
	/** Whether the trigger is being tested (for inactive feeds) */
	isTestingTrigger: boolean;
}

/** Filter options for the feed list */
export type FeedFilter =
	| 'all'
	| 'active'
	| 'inactive'
	| 'matching'
	| 'not-matching';

export interface AdminBarStore {
	feeds: AdminBarFeedState[];
	loading: boolean;
	panelOpen: boolean;
	/** Whether the current user is an admin (has manage_options capability) */
	isAdmin: boolean;
	/** Whether to persist panel state in localStorage */
	persistState: boolean;
	/** Search query for filtering feeds by name */
	searchQuery: string;
	/** Current filter for feed status */
	statusFilter: FeedFilter;
	togglePanel: () => void;
	init: (config: AdminBarConfig) => void;
	updateEvaluationResults: (
		feedId: number,
		results: GroupEvaluationResult[]
	) => void;
	updateTriggerState: (feedId: number, state: TriggerState) => void;
	updatePopupState: (feedId: number, state: PopupLifecycleState) => void;
	clearDismissedStatus: (feedId: number) => void;
	clearAllDismissedStatuses: () => void;
	calculateOverallMatch: (results: GroupEvaluationResult[]) => boolean;
	getFeed: (feedId: number) => AdminBarFeedState | undefined;
	/** Get feeds filtered by search query and status filter */
	getFilteredFeeds: () => AdminBarFeedState[];
	/** Set the search query */
	setSearchQuery: (query: string) => void;
	/** Set the status filter */
	setStatusFilter: (filter: FeedFilter) => void;
	/** Start test trigger mode for an inactive feed */
	startTestTrigger: (feedId: number) => void;
	/** Stop test trigger mode for a feed */
	stopTestTrigger: (feedId: number) => void;
}

/**
 * Simple fuzzy match - checks if all characters in query appear in text in order
 */
function fuzzyMatch(text: string, query: string): boolean {
	const lowerText = text.toLowerCase();
	const lowerQuery = query.toLowerCase();

	let textIndex = 0;
	for (const char of lowerQuery) {
		const foundIndex = lowerText.indexOf(char, textIndex);
		if (foundIndex === -1) {
			return false;
		}
		textIndex = foundIndex + 1;
	}
	return true;
}

/**
 * Create the admin bar store
 */
export function createAdminBarStore(): AdminBarStore {
	// Check if panel was previously open (from localStorage)
	const wasOpen = getInitialPanelOpenState();

	return {
		feeds: [],
		loading: true,
		panelOpen: wasOpen,
		isAdmin: true, // Default to true, will be set by init()
		persistState: true, // Default to true, will be set by init()
		searchQuery: '',
		statusFilter: 'all' as FeedFilter,

		/**
		 * Toggle the debug panel visibility and persist state (if enabled)
		 */
		togglePanel() {
			this.panelOpen = !this.panelOpen;
			if (this.persistState) {
				savePanelOpenState(this.panelOpen);
			}
		},

		/**
		 * Initialize the store with config from PHP
		 */
		init(config: AdminBarConfig) {
			if (!config || !config.feeds) {
				this.feeds = [];
				this.loading = false;
				return;
			}

			// Set admin status from config
			this.isAdmin = config.isAdmin ?? true;

			// Set persistence mode from config
			this.persistState = config.persistState ?? true;

			// Handle initial panel state
			if (!this.persistState) {
				// When persistence is disabled, always use defaultOpen (ignore localStorage)
				this.panelOpen = config.defaultOpen ?? false;
			} else if (config.defaultOpen && !getInitialPanelOpenState()) {
				// If defaultOpen is set and localStorage doesn't have a saved state, use defaultOpen
				// This allows PHP to control the initial state while still respecting user preference
				this.panelOpen = true;
			}

			this.feeds = config.feeds.map((feed) => ({
				...feed,
				evaluationResults: [],
				// Initial match is true until we evaluate (will be updated by targeting-evaluator)
				overallMatch: true,
				// Initialize trigger state from gpPopupsConfig if available
				triggerState: buildInitialTriggerState(feed.feedId),
				popupState: 'waiting' as PopupLifecycleState,
				// Initialize dismissed status from cookie
				isDismissedByCookie: feed.respectDismissal
					? getCookie(feed.cookieName) === '1'
					: false,
				// Not testing by default
				isTestingTrigger: false,
			}));
			this.loading = false;
		},

		/**
		 * Update evaluation results for a feed.
		 *
		 * The results already include server-side condition results because
		 * the condition-registry uses `serverResult` from each condition.
		 */
		updateEvaluationResults(
			feedId: number,
			results: GroupEvaluationResult[]
		) {
			const feed = this.feeds.find((f) => f.feedId === feedId);
			if (feed) {
				feed.evaluationResults = results;
				feed.overallMatch = this.calculateOverallMatch(results);
			}
		},

		/**
		 * Update trigger state for a feed.
		 */
		updateTriggerState(feedId: number, state: TriggerState) {
			const feed = this.feeds.find((f) => f.feedId === feedId);
			if (feed) {
				feed.triggerState = state;
			}
		},

		/**
		 * Update popup lifecycle state for a feed.
		 * Also updates isDismissedByCookie when state is 'dismissed' or 'submitted'.
		 *
		 * Note: 'preloading' and 'preloaded' states should not overwrite 'open' state
		 * because the iframe ready event can fire while the popup is already open.
		 */
		updatePopupState(feedId: number, state: PopupLifecycleState) {
			const feed = this.feeds.find((f) => f.feedId === feedId);
			if (feed) {
				// Don't let preload states overwrite open state
				// The iframe ready event can fire while popup is already open
				if (
					feed.popupState === 'open' &&
					(state === 'preloading' || state === 'preloaded')
				) {
					return;
				}

				feed.popupState = state;
				// Update dismissed status when popup is dismissed or submitted
				// (both set the dismissal cookie if respectDismissal is enabled)
				if (
					(state === 'dismissed' || state === 'submitted') &&
					feed.respectDismissal
				) {
					feed.isDismissedByCookie = true;
				}
			}
		},

		/**
		 * Clear the dismissed status for a feed (used when clearing cookies).
		 */
		clearDismissedStatus(feedId: number) {
			const feed = this.feeds.find((f) => f.feedId === feedId);
			if (feed) {
				feed.isDismissedByCookie = false;
			}
		},

		/**
		 * Clear all dismissed statuses (used when clearing all cookies).
		 */
		clearAllDismissedStatuses() {
			for (const feed of this.feeds) {
				feed.isDismissedByCookie = false;
			}
		},

		/**
		 * Calculate overall match from evaluation results.
		 * Uses OR logic between groups - any group matching = overall match.
		 */
		calculateOverallMatch(results: GroupEvaluationResult[]): boolean {
			// No targeting groups = show to everyone
			if (!results || results.length === 0) {
				return true;
			}

			// OR logic between groups
			return results.some((group) => group.matched);
		},

		/**
		 * Get a feed by ID
		 */
		getFeed(feedId: number): AdminBarFeedState | undefined {
			return this.feeds.find((f) => f.feedId === feedId);
		},

		/**
		 * Get feeds filtered by search query and status filter
		 */
		getFilteredFeeds(): AdminBarFeedState[] {
			let filtered = this.feeds;

			// Apply status filter
			switch (this.statusFilter) {
				case 'active':
					filtered = filtered.filter((f) => f.isActive);
					break;
				case 'inactive':
					filtered = filtered.filter((f) => !f.isActive);
					break;
				case 'matching':
					filtered = filtered.filter((f) => f.overallMatch);
					break;
				case 'not-matching':
					filtered = filtered.filter((f) => !f.overallMatch);
					break;
				// 'all' - no filtering
			}

			// Apply search query (fuzzy match on name)
			if (this.searchQuery.trim()) {
				filtered = filtered.filter((f) =>
					fuzzyMatch(f.name || '', this.searchQuery)
				);
			}

			return filtered;
		},

		/**
		 * Set the search query
		 */
		setSearchQuery(query: string) {
			this.searchQuery = query;
		},

		/**
		 * Set the status filter
		 */
		setStatusFilter(filter: FeedFilter) {
			this.statusFilter = filter;
		},

		/**
		 * Start test trigger mode for an inactive feed.
		 * This allows inactive feeds to show trigger progress during testing.
		 */
		startTestTrigger(feedId: number) {
			const feed = this.feeds.find((f) => f.feedId === feedId);
			if (feed) {
				feed.isTestingTrigger = true;
				// Ensure trigger state is initialized
				if (!feed.triggerState) {
					feed.triggerState = buildInitialTriggerState(feedId);
				}
			}
		},

		/**
		 * Stop test trigger mode for a feed.
		 * Called when the test completes or is cancelled.
		 */
		stopTestTrigger(feedId: number) {
			const feed = this.feeds.find((f) => f.feedId === feedId);
			if (feed) {
				feed.isTestingTrigger = false;
				// Reset trigger state to initial
				feed.triggerState = buildInitialTriggerState(feedId);
			}
		},
	};
}
